/**
 * Image processing utilities for minister profile photos
 * Handles resize, compression, and format conversion
 */

const MAX_DIMENSION = 800;
const JPEG_QUALITY = 0.85;

/**
 * Resizes and compresses an image file
 * - JPEG: Resizes to max 800x800 and compresses to 85% quality
 * - PNG: Resizes to max 800x800, keeps PNG format (no quality loss)
 */
export async function processImage(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    img.onload = () => {
      try {
        // Calculate new dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = (height * MAX_DIMENSION) / width;
            width = MAX_DIMENSION;
          } else {
            width = (width * MAX_DIMENSION) / height;
            height = MAX_DIMENSION;
          }
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Error al procesar la imagen'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with appropriate format
        const isJPEG = file.type === 'image/jpeg' || file.type === 'image/jpg';
        const outputType = isJPEG ? 'image/jpeg' : 'image/png';
        const quality = isJPEG ? JPEG_QUALITY : undefined;

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Error al comprimir la imagen'));
              return;
            }

            // Create new file with same name and extension
            const processedFile = new File([blob], file.name, {
              type: outputType,
              lastModified: Date.now(),
            });

            resolve(processedFile);
          },
          outputType,
          quality
        );
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Error al cargar la imagen'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Converts a File to ArrayBuffer (for bytea column)
 */
export async function fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = () => {
      resolve(reader.result as ArrayBuffer);
    };
    
    reader.onerror = () => {
      reject(new Error('Error al convertir la imagen a formato binario'));
    };
    
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Converts ArrayBuffer to hex string for PostgreSQL bytea column
 * PostgreSQL expects format like: '\xFFD8FFE0...'
 */
export function arrayBufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let hex = '\\x';
  for (let i = 0; i < bytes.length; i++) {
    const byte = bytes[i].toString(16).padStart(2, '0');
    hex += byte;
  }
  return hex;
}

/**
 * Gets file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  return parts.length > 1 ? `.${parts[parts.length - 1]}` : '';
}

/**
 * Constructs filename for storage based on adb_code and original file extension
 */
export function constructFilename(adbCode: string, originalFilename: string): string {
  const extension = getFileExtension(originalFilename);
  return `${adbCode}${extension}`;
}

/**
 * Constructs public URL for profile photo from Supabase Storage
 */
export function constructPhotoUrl(supabaseUrl: string, filename: string | null): string | null {
  if (!filename) return null;
  return `${supabaseUrl}/storage/v1/object/public/MinisterImages/ProfilePhotos/${filename}`;
}
